import { LDFlagValue } from 'launchdarkly-js-client-sdk';

export interface GtmCanvasGateParams {
  adsIntelEnabled: LDFlagValue;
  buyingCenterEnabled: LDFlagValue;
  generatedContentEnabled: LDFlagValue;
  hasCustomAudience: boolean;
  hasBuyingCenter: boolean;
  hasCrmExport: boolean;
  hasGeneratedContent: boolean;
}

/**
 * Shape of an automation used for GTMCanvas gating checks.
 * This is intentionally structural so it can be used with different automation DTOs.
 */
export interface GtmCanvasGateAutomation {
  custom_audience?: unknown;
  job_departments?: unknown;
  job_sub_departments?: unknown;
  keywords?: unknown;
  job_levels?: unknown;
  export_to_crm?: unknown;
  content_type?: unknown;
  subject_line_mapping_field?: unknown;
  email_content_mapping_field?: unknown;
  call_script_mapping_field?: unknown;
  connection_request_mapping_field?: unknown;
  additional_context?: unknown;
}

function hasNonEmptyLength(value: unknown): boolean {
  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length > 0;
  }
  return false;
}

/**
 * Returns the composite gating condition for GTMCanvas editing.
 *
 * The decision is based on:
 * - AdsIntel ICP Agent feature flag and presence of a custom audience
 * - Buying Center feature flag and Buying Center configuration **without** a CRM export
 */
export function getGtmCanvasGatingCondition(params: GtmCanvasGateParams): boolean {
  const {
    adsIntelEnabled,
    buyingCenterEnabled,
    generatedContentEnabled,
    hasCustomAudience,
    hasBuyingCenter,
    hasCrmExport,
    hasGeneratedContent
  } = params;

  const hasBuyingCenterWithoutCrm: boolean = hasBuyingCenter && !hasCrmExport;

  return (
    (Boolean(adsIntelEnabled) && hasCustomAudience) ||
    (Boolean(buyingCenterEnabled) && hasBuyingCenterWithoutCrm) ||
    (Boolean(generatedContentEnabled) && hasGeneratedContent)
  );
}

/**
 * Convenience helper: builds the composite gating condition from a full automation object
 * and the relevant feature flags.
 */
export function getGtmCanvasGatingConditionForAutomation(
  automation: GtmCanvasGateAutomation | null | undefined,
  adsIntelEnabled: LDFlagValue,
  buyingCenterEnabled: LDFlagValue,
  generatedContentEnabled: LDFlagValue,
): boolean {
  const hasCustomAudience: boolean = !!automation?.custom_audience;
  const hasBuyingCenter: boolean = !!(
    hasNonEmptyLength(automation?.job_departments) ||
    hasNonEmptyLength(automation?.job_sub_departments) ||
    hasNonEmptyLength(automation?.keywords) ||
    hasNonEmptyLength(automation?.job_levels)
  );
  const hasCrmExport: boolean = !!automation?.export_to_crm;
  const hasGeneratedContent: boolean = !!(
    hasNonEmptyLength(automation?.content_type) ||
    hasNonEmptyLength(automation?.subject_line_mapping_field) ||
    hasNonEmptyLength(automation?.email_content_mapping_field) ||
    hasNonEmptyLength(automation?.call_script_mapping_field) ||
    hasNonEmptyLength(automation?.connection_request_mapping_field) ||
    hasNonEmptyLength(automation?.additional_context)
  );

  return getGtmCanvasGatingCondition({
    adsIntelEnabled,
    buyingCenterEnabled,
    generatedContentEnabled,
    hasCustomAudience,
    hasBuyingCenter,
    hasCrmExport,
    hasGeneratedContent
  });
}

/**
 * Determines whether an automation should be gated to GTMCanvas editing only.
 *
 * The gating decision is composed of:
 * - GTMCanvas feature flag state (must be enabled)
 * - A caller-provided gating condition, such as the result of `getGtmCanvasGatingCondition`.
 */
export function shouldGateGtmCanvasEdit(gtmCanvasEnabled: LDFlagValue, gatingCondition: boolean): boolean {
  return Boolean(gtmCanvasEnabled) && Boolean(gatingCondition);
}


